import React from 'react';
import { TextField, Tooltip, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller } from "react-hook-form";
import { alpha } from "@mui/material/styles";
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers';
import moment from "moment";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Leads } from '../../api/Endpoints/Leads';
import { toast } from 'react-toastify';
const DateInput = (props) => {
    const { id, name, control, label, setValue, func, readOnly } = props;
    const compactTable = props.compactTable === true;
    let textFiledStyles = {
        '& .MuiFilledInput-root': {
            backgroundColor: 'background.paper',
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: 'neutral.300',
            borderRadius: 1,
            boxShadow: '0px 1px 2px 0px rgba(9, 30, 66, 0.08)',
            overflow: 'hidden',
            p: 0,
            transition: (theme) => theme.transitions.create([
                'border-color',
                'box-shadow'
            ]),
            '&:before': {
                borderBottom: 0
            },
            '&:hover': {
                backgroundColor: 'background.paper'
            },
            '&.Mui-focused': {
                backgroundColor: 'background.paper',
                boxShadow: (theme) => `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`
            },
            '&.Mui-disabled': {
                backgroundColor: 'action.disabledBackground',
                boxShadow: 'none',
                borderColor: alpha('#D6DBE1', 0.5)
            },
            '.MuiInputAdornment-root.MuiInputAdornment-positionStart.MuiInputAdornment-root:not(.MuiInputAdornment-hiddenLabel)': {
                mt: 0,
                ml: 1.5
            }
        },
    };

    let InputLabelProps = {
        shrink: true,
        sx: {
            color: 'text.primary',
            fontSize: 14,
            fontWeight: 500,
            mb: 0.5,
            position: 'relative',
            transform: 'none'
        }
    };

    let inputProps = {
        sx: {
            alignItems: 'center',
            display: 'flex',
            fontSize: 14,
            height: 'unset',
            lineHeight: 1.6,
            px: 1.5,
            py: 0.75,
            '&.MuiInputBase-inputAdornedStart': {
                pl: 0
            }
        }
    };

    const handleDateChange = (date) => {
        const formattedDate = (date && moment(date).isValid()) ? moment(date).format('YYYY-MM-DD') : null;
        setValue(name, formattedDate);
        
        const updateData = {
            id: id,
            follow_up_date: formattedDate
        };

        Leads.update(updateData).then((response) => {
            const resultDate = response.data.lead.follow_up_date;
            toast.success(
                <span>
                    The Follow Up Date has been {resultDate ? <>updated to <strong>{moment(resultDate).format('DD-MM-YYYY')}</strong></> : 'removed'}
                </span>,
                { autoClose: 2000 }
            );
            props.func && props.func();
        }).catch((err) => {
            console.error(err);
        });
    };

    const [pickerOpen, setPickerOpen] = React.useState(false);

    const openPicker = (e) => {
        e.stopPropagation();
        setPickerOpen(true);
    };

    if (compactTable) {
        return (
            <Controller
                name={name}
                control={control}
                defaultValue={props.value || ''}
                render={({ field: { value } }) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', position: 'relative' }}>
                            {/* Hidden MUI date picker */}
                            {!readOnly && (
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DesktopDatePicker
                                        open={pickerOpen}
                                        onOpen={() => setPickerOpen(true)}
                                        onClose={() => setPickerOpen(false)}
                                        value={value ? moment(value) : null}
                                        onChange={(newValue) => {
                                            const formattedDate = (newValue && moment(newValue).isValid()) ? moment(newValue).format('YYYY-MM-DD') : null;
                                            setValue(name, formattedDate);
                                        }}
                                        onAccept={(newValue) => {
                                            handleDateChange(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField 
                                                {...params} 
                                                sx={{ 
                                                    position: 'absolute',
                                                    opacity: 0,
                                                    width: 0,
                                                    height: 0,
                                                    padding: 0,
                                                    margin: 0,
                                                    border: 'none',
                                                    pointerEvents: 'none'
                                                }} 
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            )}

                            {value ? (
                                /* ── Date is set: show a compact indigo chip ── */
                                <Box sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    backgroundColor: '#EEF2FF',
                                    border: '1px solid #C7D2FE',
                                    borderRadius: '20px',
                                    px: '8px',
                                    py: '3px',
                                }}>
                                    <Tooltip title={readOnly ? "" : "Click to change date"} arrow>
                                        <Box
                                            onClick={readOnly ? undefined : openPicker}
                                            sx={{
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                color: '#4338CA',
                                                cursor: readOnly ? 'default' : 'pointer',
                                                whiteSpace: 'nowrap',
                                                letterSpacing: '0.3px',
                                                lineHeight: 1,
                                                userSelect: 'none',
                                            }}
                                        >
                                            📅 {moment(value).format('DD MMM YY')}
                                        </Box>
                                    </Tooltip>

                                    {!readOnly && (
                                        <Tooltip title="Remove Date" arrow>
                                            <Box
                                                onClick={(e) => { e.stopPropagation(); handleDateChange(null); }}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                    color: '#818CF8',
                                                    '&:hover': { color: '#EF4444' },
                                                    transition: 'color 0.15s ease',
                                                }}
                                            >
                                                <CloseIcon sx={{ fontSize: 12 }} />
                                            </Box>
                                        </Tooltip>
                                    )}
                                </Box>
                            ) : (
                                /* ── No date: show a dashed pill ── */
                                <Tooltip title="Set Follow Up Date" arrow>
                                    <Box
                                        onClick={openPicker}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#F8FAFC',
                                            border: '1px dashed #CBD5E1',
                                            borderRadius: '20px',
                                            px: '10px',
                                            py: '4px',
                                            cursor: 'pointer',
                                            fontSize: '11px',
                                            color: '#94A3B8',
                                            fontWeight: 500,
                                            gap: '4px',
                                            whiteSpace: 'nowrap',
                                            userSelect: 'none',
                                            '&:hover': {
                                                backgroundColor: '#EEF2FF',
                                                borderColor: '#818CF8',
                                                color: '#4338CA',
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        📅 Set date
                                    </Box>
                                </Tooltip>
                            )}
                        </Box>
                )}
            />
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Controller
                name={name}
                control={control}
                defaultValue={props.value}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <DesktopDatePicker
                        label={label}
                        value={value ? moment(value) : null}
                        onChange={(newValue) => {
                            const formattedDate = (newValue && moment(newValue).isValid()) ? moment(newValue).format('YYYY-MM-DD') : null;
                            setValue(name, formattedDate);
                        }}
                        onAccept={(newValue) => {
                            handleDateChange(newValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                sx={textFiledStyles}
                                InputLabelProps={InputLabelProps}
                                placeholder='DD-MM-YYYY'
                                variant="filled"
                                value={value ? moment(value).format('DD-MM-YYYY') : ''}
                                helperText={error ? error.message : null}
                                error={!!error}
                                inputProps={inputProps}
                                style={{ cursor: 'none' }}
                            />
                        )}
                    />
                )}
            />
        </LocalizationProvider>
    );
};

export default DateInput;
